// Every change to the data, as a pure function: (state, ...) → new state. The screens call these through the store,
// which keeps undo/redo history and syncs with the server. Each one checks its input so a slip of the finger can't
// leave the data in a broken state; anything that can't be done throws an Error with a message for the person.

import { r2 } from './money.js';
import { isMonth, isDate, addMonths, monthOf } from './calendar.js';
import { newId, versionAt, defaultPerson, PERSON_COLORS } from './state.js';
import { snapshotOf } from './model.js';
import { validateParams } from './tax/params.js';

const fail = (msg) => { throw new Error(msg); };
const finite = (v) => typeof v === 'number' && Number.isFinite(v);
const clean = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined));

/* ── Month reviews ─────────────────────────────────────── */

function setReviewRaw(state, month, pid, fn) {
  if (!isMonth(month)) fail('Unknown month');
  if (!state.people.some((p) => p.id === pid)) fail('Unknown person');
  const m = state.months[month] || {};
  const people = { ...(m.people || {}) };
  const next = fn({ ...(people[pid] || {}) });
  if (next === null) delete people[pid];
  else people[pid] = next;
  const nm = { ...m, people };
  const months = { ...state.months, [month]: nm };
  if (!Object.keys(people).length && !nm.expenses && !nm.note) delete months[month];
  return { ...state, months };
}

const REVIEW_FIELDS = ['sickDays', 'absenceDays', 'vacationDays', 'mealDays', 'commissionPct', 'extraPay', 'actualNet', 'actualMeal',
  'reimbursed', 'refundActual', 'extraIncome', 'extraSpending', 'note'];

function checkReviewPatch(patch) {
  for (const [k, v] of Object.entries(patch)) {
    if (!REVIEW_FIELDS.includes(k)) fail(`Can't change ${k}`);
    if (['sickDays', 'absenceDays', 'vacationDays'].includes(k) && v !== null && (!finite(v) || v < 0 || v > 31)) fail('Days must be between 0 and 31');
    if (k === 'mealDays' && v !== null && v !== undefined && (!finite(v) || v < 0 || v > 31)) fail('Meal days must be between 0 and 31');
    if (k === 'commissionPct' && v !== null && (!finite(v) || v < 0 || v > 1000)) fail('Commission must be between 0% and 1000%');
    if (['actualNet', 'actualMeal', 'reimbursed'].includes(k) && v !== null && (!finite(v) || v < 0)) fail("Amounts received can't be negative");
    if (k === 'refundActual' && v !== null && !finite(v)) fail('Not an amount');
    if (['extraPay', 'extraIncome', 'extraSpending'].includes(k) && v !== null && v !== undefined) {
      if (!Array.isArray(v)) fail('Expected a list');
      for (const it of v) if (!it || !finite(it.amount)) fail('Every line needs an amount');
    }
    if (k === 'note' && typeof v !== 'string') fail('Notes must be text');
  }
}

// Change what happened in a month (doesn't confirm it). Values of null clear a field.
export function updateReview(state, month, pid, patch) {
  checkReviewPatch(patch);
  return setReviewRaw(state, month, pid, (r) => {
    const next = { ...r };
    for (const [k, v] of Object.entries(patch)) {
      if (v === null || v === undefined || (Array.isArray(v) && !v.length) || (k === 'note' && !v.trim())) delete next[k];
      else next[k] = Array.isArray(v) ? v.map((x) => ({ id: x.id || newId('i'), label: String(x.label || '').slice(0, 120), ...clean({ taxable: x.taxable }), amount: r2(x.amount) })) : v;
    }
    return Object.keys(next).length ? next : null;
  });
}

// Confirm a payday. Blank actual amounts take the expected ones, so a confirmed month never changes afterwards.
export function confirmReview(state, model, month, pid, { by = null, now = Date.now() } = {}) {
  const entry = model.byKey[month]?.people.find((e) => e.personId === pid);
  if (!entry) fail('That month is not in the plan');
  const r = state.months[month]?.people?.[pid] || {};
  return setReviewRaw(state, month, pid, () => ({
    ...r,
    actualNet: finite(r.actualNet) ? r.actualNet : entry.actual.net,
    actualMeal: finite(r.actualMeal) ? r.actualMeal : entry.actual.meal,
    ...(entry.expected.refund || finite(r.refundActual) ? { refundActual: finite(r.refundActual) ? r.refundActual : entry.expected.refund } : {}),
    confirmed: true,
    confirmedAt: now,
    confirmedBy: by,
    snapshot: snapshotOf(entry),
  }));
}

// Back to pending, keeping what was typed
export function reopenReview(state, month, pid) {
  return setReviewRaw(state, month, pid, (r) => {
    const { confirmed, confirmedAt, confirmedBy, snapshot, ...rest } = r;
    return Object.keys(rest).length ? rest : null;
  });
}

// Forget everything typed for this payday
export function resetReview(state, month, pid) {
  return setReviewRaw(state, month, pid, () => null);
}

// Confirm every pending payday as expected (for catching up after a while)
export function confirmAllPending(state, model, opts) {
  let s = state;
  for (const e of model.pending) s = confirmReview(s, model, e.month, e.personId, opts);
  return s;
}

/* ── Month-level: expense actuals and notes ────────────── */

export function setExpenseActual(state, month, expenseId, amount) {
  if (!isMonth(month)) fail('Unknown month');
  if (!state.expenses.some((e) => e.id === expenseId)) fail('Unknown expense');
  if (amount !== null && (!finite(amount) || amount < 0)) fail("Expenses can't be negative");
  const m = { ...(state.months[month] || {}) };
  const ex = { ...(m.expenses || {}) };
  if (amount === null) delete ex[expenseId];
  else ex[expenseId] = r2(amount);
  if (Object.keys(ex).length) m.expenses = ex;
  else delete m.expenses;
  const months = { ...state.months, [month]: m };
  if (!m.people && !m.expenses && !m.note) delete months[month];
  return { ...state, months };
}

export function setMonthNote(state, month, note) {
  if (!isMonth(month)) fail('Unknown month');
  const m = { ...(state.months[month] || {}) };
  if (note && note.trim()) m.note = note.slice(0, 2000);
  else delete m.note;
  const months = { ...state.months, [month]: m };
  if (!m.people && !m.expenses && !m.note) delete months[month];
  return { ...state, months };
}

/* ── Things that change from a given month on (plans, allowances, expense amounts) ── */

// Put a version starting at `from` into a sorted list, replacing one that starts the same month
function putVersion(list, version) {
  const rest = (list || []).filter((v) => v.from !== version.from);
  return [...rest, version].sort((a, b) => (a.from < b.from ? -1 : 1));
}

/* People */

export function addPerson(state, { name, since = null } = {}) {
  const n = String(name || '').trim();
  if (!n) fail('Give them a name');
  if (since !== null && !isMonth(since)) fail('Pick the month they join');
  const used = new Set(state.people.map((p) => p.color));
  const color = PERSON_COLORS.find((c) => !used.has(c)) || PERSON_COLORS[state.people.length % PERSON_COLORS.length];
  const from = since || state.settings.startMonth;
  const person = defaultPerson({ name: n.slice(0, 60), color, plan: { id: newId('plan'), from, mode: 'net', net: 0, mealCard: 0 } });
  person.since = since;
  return { ...state, people: [...state.people, person] };
}

const PERSON_FIELDS = ['name', 'color', 'email', 'birthDate', 'since', 'until', 'payday', 'holidays', 'irsJovem', 'personalDeductions'];

export function updatePerson(state, pid, patch) {
  const p = state.people.find((x) => x.id === pid);
  if (!p) fail('Unknown person');
  for (const k of Object.keys(patch)) if (!PERSON_FIELDS.includes(k)) fail(`Can't change ${k}`);
  if ('email' in patch) patch = { ...patch, email: String(patch.email || '').trim().toLowerCase() };
  if ('name' in patch && !String(patch.name || '').trim()) fail("The name can't be empty");
  if ('birthDate' in patch && patch.birthDate && !isDate(patch.birthDate)) fail('Not a valid date');
  if ('email' in patch && patch.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(patch.email)) fail('Not a valid email address');
  if ('since' in patch && patch.since !== null && !isMonth(patch.since)) fail('Not a valid month');
  if ('until' in patch && patch.until !== null && !isMonth(patch.until)) fail('Not a valid month');
  const since = 'since' in patch ? patch.since : p.since;
  const until = 'until' in patch ? patch.until : p.until;
  if (since && until && until < since) fail('They have to leave after they join');
  if ('personalDeductions' in patch && (!finite(patch.personalDeductions) || patch.personalDeductions < 0)) fail('Not a valid amount');
  if ('payday' in patch) {
    const pd = patch.payday;
    if (!pd || !['last-business-day', 'day', 'first-business-day-next'].includes(pd.type)) fail('Pick when they get paid');
    if (pd.type === 'day' && (!Number.isInteger(pd.day) || pd.day < 1 || pd.day > 31)) fail('Payday must be a day from 1 to 31');
    if (pd.type === 'day' && pd.shift && !['before', 'after', 'none'].includes(pd.shift)) fail('Pick what happens on weekends');
  }
  if ('irsJovem' in patch) {
    const j = patch.irsJovem || {};
    if (j.enabled && !(Number.isInteger(j.firstYear) && j.firstYear >= 1990 && j.firstYear <= 2100)) fail('Enter the first year of work income');
    if (j.skipYears && !j.skipYears.every((y) => Number.isInteger(y))) fail('Years must be whole numbers');
  }
  const next = { ...p, ...patch };
  if ('name' in patch) next.name = String(patch.name).trim().slice(0, 60);
  if (next.email && state.people.some((x) => x.id !== pid && x.email && x.email === next.email)) fail('Someone else already uses that email');
  return { ...state, people: state.people.map((x) => (x.id === pid ? next : x)) };
}

// Removing someone keeps their past: they simply stop taking part after `lastMonth`. Removing someone who never had
// anything recorded deletes them outright.
export function removePerson(state, pid, lastMonth) {
  const p = state.people.find((x) => x.id === pid);
  if (!p) fail('Unknown person');
  const others = state.people.filter((x) => x.id !== pid && !x.until);
  if (!others.length) fail("There has to be at least one person");
  const hasHistory = Object.values(state.months).some((m) => m.people?.[pid]) || state.expenses.some((e) => e.owner === pid);
  if (!hasHistory && !lastMonth) {
    return { ...state, people: state.people.filter((x) => x.id !== pid), settings: dropCustomShare(state.settings, pid) };
  }
  if (!isMonth(lastMonth)) fail('Pick the last month they take part in');
  return updatePerson(state, pid, { until: lastMonth });
}

const dropCustomShare = (settings, pid) => {
  const custom = { ...(settings.split?.custom || {}) };
  delete custom[pid];
  return { ...settings, split: { ...settings.split, custom } };
};

export function movePerson(state, pid, dir) {
  const i = state.people.findIndex((p) => p.id === pid);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= state.people.length) return state;
  const people = [...state.people];
  [people[i], people[j]] = [people[j], people[i]];
  return { ...state, people };
}

/* Income plans */

function checkPlan(plan) {
  if (!plan || !['employee', 'net', 'none'].includes(plan.mode)) fail('Pick a kind of income');
  const money = (v, label) => { if (v !== undefined && (!finite(v) || v < 0)) fail(`${label} must be a positive amount`); };
  if (plan.mode === 'employee') {
    if (plan.allowanceType !== undefined && !['fixed', 'reimbursement'].includes(plan.allowanceType)) fail('Pick what kind of allowance it is');
    money(plan.base, 'Base salary'); money(plan.commission, 'Commission'); money(plan.mealPerDay, 'Meal allowance');
    money(plan.allowance, 'Allowance');
    if (plan.mealPerDay > 100) fail('Meal allowance is per day — that looks too high');
    if (plan.startDate && !isDate(plan.startDate)) fail('Not a valid start date');
    if (plan.endDate && !isDate(plan.endDate)) fail('Not a valid end date');
    if (plan.startDate && plan.endDate && plan.endDate < plan.startDate) fail('The contract has to end after it starts');
    if (plan.ssRate !== undefined && (!finite(plan.ssRate) || plan.ssRate < 0 || plan.ssRate > 50)) fail('Social security rate looks wrong');
    if (plan.dependents !== undefined && (!Number.isInteger(plan.dependents) || plan.dependents < 0 || plan.dependents > 20)) fail('Dependants must be a whole number');
    if (plan.table && !['I', 'II', 'III'].includes(plan.table)) fail('Unknown tax table');
    const sub = plan.subsidies || {};
    for (const k of ['holidayMonth', 'christmasMonth']) if (sub[k] !== undefined && !(Number.isInteger(sub[k]) && sub[k] >= 1 && sub[k] <= 12)) fail('Bonus months must be 1–12');
    for (const d of plan.deductions || []) if (!finite(d.pct ?? 0) || !finite(d.fixed ?? 0)) fail('Every deduction needs a number');
    money(plan.yearlyBonus, 'Yearly bonus');
    for (const k of ['letterIrsBase', 'letterIrsCommission', 'letterNetYear']) if (plan[k] !== undefined && plan[k] !== null) money(plan[k], 'The offer letter figures');
    if (plan.yearlyBonusMonth !== undefined && !(Number.isInteger(plan.yearlyBonusMonth) && plan.yearlyBonusMonth >= 1 && plan.yearlyBonusMonth <= 12)) fail('Pick the month the yearly bonus is paid');
    if (plan.commissionSS !== undefined && typeof plan.commissionSS !== 'boolean') fail('Pick whether commission pays social security');
  }
  if (plan.mode === 'net') {
    money(plan.net, 'Take-home pay'); money(plan.mealCard, 'Meal card');
  }
}

// Set the income plan from a month on. If a plan already starts that month it's replaced; earlier months keep theirs.
export function setPlanFrom(state, pid, from, plan) {
  if (!isMonth(from)) fail('Pick the month this starts');
  checkPlan(plan);
  const p = state.people.find((x) => x.id === pid);
  if (!p) fail('Unknown person');
  const existing = (p.plans || []).find((x) => x.from === from);
  const version = { ...plan, id: existing?.id || plan.id || newId('plan'), from };
  return { ...state, people: state.people.map((x) => (x.id === pid ? { ...x, plans: putVersion(x.plans, version) } : x)) };
}

// Edit one existing plan in place (all months it covers)
export function updatePlan(state, pid, planId, patch) {
  const p = state.people.find((x) => x.id === pid);
  const plan = p?.plans.find((x) => x.id === planId);
  if (!plan) fail('Unknown plan');
  const next = { ...plan, ...patch, id: plan.id };
  checkPlan(next);
  if ('from' in patch) {
    if (!isMonth(patch.from)) fail('Not a valid month');
    if (p.plans.some((x) => x.id !== planId && x.from === patch.from)) fail('Another plan already starts that month');
  }
  return {
    ...state,
    people: state.people.map((x) => (x.id === pid ? { ...x, plans: x.plans.map((v) => (v.id === planId ? next : v)).sort((a, b) => (a.from < b.from ? -1 : 1)) } : x)),
  };
}

export function deletePlan(state, pid, planId) {
  const p = state.people.find((x) => x.id === pid);
  if (!p?.plans.some((x) => x.id === planId)) fail('Unknown plan');
  if (p.plans.length === 1) fail('Everyone needs at least one income plan — change it instead');
  return { ...state, people: state.people.map((x) => (x.id === pid ? { ...x, plans: x.plans.filter((v) => v.id !== planId) } : x)) };
}

/* Personal spending money */

export function setAllowanceFrom(state, pid, from, amount) {
  if (!isMonth(from)) fail('Pick the month this starts');
  if (!finite(amount) || amount < 0) fail("Can't be negative");
  const p = state.people.find((x) => x.id === pid);
  if (!p) fail('Unknown person');
  const existing = (p.allowance || []).find((x) => x.from === from);
  const version = { id: existing?.id || newId('a'), from, amount: r2(amount) };
  return { ...state, people: state.people.map((x) => (x.id === pid ? { ...x, allowance: putVersion(x.allowance, version) } : x)) };
}

export function deleteAllowanceVersion(state, pid, versionId) {
  return { ...state, people: state.people.map((x) => (x.id === pid ? { ...x, allowance: (x.allowance || []).filter((v) => v.id !== versionId) } : x)) };
}

/* Expenses */

function checkExpense(e, state) {
  if (!String(e.name || '').trim()) fail('Give the expense a name');
  if (!['month', 'year', 'once'].includes(e.every)) fail('Pick how often it happens');
  if (e.every === 'year' && !(Number.isInteger(e.dueMonth) && e.dueMonth >= 1 && e.dueMonth <= 12)) fail('Pick the month it is paid');
  if (e.every === 'once' && !isMonth(e.onMonth)) fail('Pick the month it is paid');
  if (e.owner !== 'joint' && !state.people.some((p) => p.id === e.owner)) fail('Pick who pays it');
  if (e.end !== null && e.end !== undefined && !isMonth(e.end)) fail('Not a valid end month');
}

export function addExpense(state, { name, category = 'Other', owner = 'joint', every = 'month', dueMonth, onMonth, spread = false, amount, from, end = null }) {
  if (!finite(amount) || amount < 0) fail('Enter an amount');
  const start = every === 'once' ? onMonth : from;
  if (!isMonth(start)) fail('Pick the month it starts');
  const e = { id: newId('e'), name: String(name || '').trim().slice(0, 80), category, owner, every, ...(every === 'year' ? { dueMonth, spread: !!spread } : {}), ...(every === 'once' ? { onMonth } : {}), end, versions: [{ from: start, amount: r2(amount) }] };
  checkExpense(e, state);
  if (end && end < start) fail('It has to end after it starts');
  return { ...state, expenses: [...state.expenses, e] };
}

// Name, category, who pays, how often — applies to all months
export function updateExpense(state, id, patch) {
  const e = state.expenses.find((x) => x.id === id);
  if (!e) fail('Unknown expense');
  const next = { ...e, ...patch };
  if ('name' in patch) next.name = String(patch.name || '').trim().slice(0, 80);
  checkExpense(next, state);
  if (next.end && next.end < next.versions[0].from) fail('It has to end after it starts');
  if (next.every === 'once' && next.onMonth !== e.onMonth) next.versions = [{ from: next.onMonth, amount: next.versions[next.versions.length - 1].amount }];
  return { ...state, expenses: state.expenses.map((x) => (x.id === id ? next : x)) };
}

// A new amount from a month on; earlier months keep the old amount
export function setExpenseAmountFrom(state, id, from, amount) {
  if (!isMonth(from)) fail('Pick the month the new amount starts');
  if (!finite(amount) || amount < 0) fail("Can't be negative");
  const e = state.expenses.find((x) => x.id === id);
  if (!e) fail('Unknown expense');
  let versions;
  if (e.every === 'once') versions = [{ from: e.onMonth, amount: r2(amount) }];
  else if (from < e.versions[0].from) versions = [{ from, amount: r2(amount) }, ...e.versions.filter((v) => v.from > from)];
  else versions = putVersion(e.versions, { from, amount: r2(amount) });
  // Drop versions that repeat the amount before them
  versions = versions.filter((v, i) => i === 0 || v.amount !== versions[i - 1].amount);
  return { ...state, expenses: state.expenses.map((x) => (x.id === id ? { ...x, versions } : x)) };
}

// Stop an expense after a month (keeps its history), or delete it entirely if it never applied before `today`
export function endExpense(state, id, lastMonth) {
  if (!isMonth(lastMonth)) fail('Pick the last month');
  const e = state.expenses.find((x) => x.id === id);
  if (!e) fail('Unknown expense');
  if (lastMonth < e.versions[0].from) fail('That is before it started');
  return { ...state, expenses: state.expenses.map((x) => (x.id === id ? { ...x, end: lastMonth } : x)) };
}

export function deleteExpense(state, id) {
  if (!state.expenses.some((x) => x.id === id)) fail('Unknown expense');
  const months = {};
  for (const [k, m] of Object.entries(state.months)) {
    if (m.expenses && id in m.expenses) {
      const ex = { ...m.expenses };
      delete ex[id];
      const nm = { ...m };
      if (Object.keys(ex).length) nm.expenses = ex; else delete nm.expenses;
      if (nm.people || nm.expenses || nm.note) months[k] = nm;
    } else months[k] = m;
  }
  return { ...state, expenses: state.expenses.filter((x) => x.id !== id), months };
}

export const currentAmount = (e, month) => versionAt(e.versions, month)?.amount ?? e.versions[0]?.amount ?? 0;

/* ── Savings ───────────────────────────────────────────── */

export function setOpening(state, amount, note) {
  if (!finite(amount)) fail('Enter an amount');
  return { ...state, savings: { ...state.savings, opening: r2(amount), ...(note !== undefined ? { openingNote: String(note).slice(0, 200) } : {}) } };
}

export function addTransaction(state, { date, amount, label }) {
  if (!isDate(date)) fail('Pick a date');
  if (!finite(amount) || amount === 0) fail('Enter an amount (negative to take money out)');
  const t = { id: newId('t'), date, amount: r2(amount), label: String(label || '').trim().slice(0, 120) || (amount < 0 ? 'Withdrawal' : 'Deposit') };
  return { ...state, savings: { ...state.savings, transactions: [...state.savings.transactions, t].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0)) } };
}

export function updateTransaction(state, id, patch) {
  const t = state.savings.transactions.find((x) => x.id === id);
  if (!t) fail('Unknown entry');
  const next = { ...t, ...patch };
  if (!isDate(next.date)) fail('Pick a date');
  if (!finite(next.amount) || next.amount === 0) fail('Enter an amount');
  next.amount = r2(next.amount);
  return { ...state, savings: { ...state.savings, transactions: state.savings.transactions.map((x) => (x.id === id ? next : x)).sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0)) } };
}

export function deleteTransaction(state, id) {
  return { ...state, savings: { ...state.savings, transactions: state.savings.transactions.filter((x) => x.id !== id) } };
}

export function addGoal(state, { name, target, by = null }) {
  if (!String(name || '').trim()) fail('Give the goal a name');
  if (!finite(target) || target <= 0) fail('Enter a target amount');
  if (by !== null && !isMonth(by)) fail('Not a valid month');
  return { ...state, savings: { ...state.savings, goals: [...state.savings.goals, { id: newId('g'), name: String(name).trim().slice(0, 60), target: r2(target), by }] } };
}

export function updateGoal(state, id, patch) {
  const g = state.savings.goals.find((x) => x.id === id);
  if (!g) fail('Unknown goal');
  const next = { ...g, ...patch };
  if (!String(next.name || '').trim()) fail('Give the goal a name');
  if (!finite(next.target) || next.target <= 0) fail('Enter a target amount');
  if (next.by !== null && next.by !== undefined && !isMonth(next.by)) fail('Not a valid month');
  return { ...state, savings: { ...state.savings, goals: state.savings.goals.map((x) => (x.id === id ? next : x)) } };
}

export function deleteGoal(state, id) {
  return { ...state, savings: { ...state.savings, goals: state.savings.goals.filter((x) => x.id !== id) } };
}

/* ── Settings ──────────────────────────────────────────── */

export function updateSettings(state, patch) {
  const next = { ...state.settings, ...patch };
  if (!isMonth(next.startMonth)) fail('Pick the first month');
  if (!Number.isInteger(next.horizon) || next.horizon < 1 || next.horizon > 120) fail('Look-ahead must be 1–120 months');
  if (!['equal', 'income', 'custom'].includes(next.split?.mode)) fail('Pick how to split joint expenses');
  if (next.split.mode === 'custom') {
    const vals = Object.values(next.split.custom || {});
    if (vals.some((v) => !finite(v) || v < 0)) fail('Shares must be positive numbers');
    if (!vals.some((v) => v > 0)) fail('At least one share has to be above zero');
  }
  if (next.irsRefund && !(Number.isInteger(next.irsRefund.month) && next.irsRefund.month >= 1 && next.irsRefund.month <= 12)) fail('Pick the month the refund usually arrives');
  return { ...state, settings: next };
}

export function markSetupDone(state, key, done = true) {
  const set = new Set(state.settings.setupDone || []);
  if (done) set.add(key); else set.delete(key);
  return { ...state, settings: { ...state.settings, setupDone: [...set] } };
}

export function setTaxYear(state, year, params) {
  if (!Number.isInteger(year) || year < 2000 || year > 2100) fail('Not a valid year');
  const errs = validateParams(params);
  if (errs.length) fail(errs[0]);
  return { ...state, taxYears: { ...state.taxYears, [year]: { ...params, year } } };
}

export function removeTaxYear(state, year) {
  const t = { ...state.taxYears };
  delete t[year];
  return { ...state, taxYears: t };
}

/* ── Spreadsheet import ────────────────────────────────── */

// changes: [{ month, field, to }] from sheetImport.diffImport. Imported amounts mark the payday as confirmed when
// the take-home pay is there (whoever filled the spreadsheet has checked it).
export function applyImport(state, model, pid, changes, { by = 'spreadsheet', now = Date.now() } = {}) {
  let s = state;
  const byMonth = {};
  for (const c of changes) (byMonth[c.month] = byMonth[c.month] || []).push(c);
  for (const [month, list] of Object.entries(byMonth)) {
    const patch = {};
    for (const c of list) {
      if (c.field === 'extraIncome' || c.field === 'extraSpending') patch[c.field] = c.to === null ? null : c.items ? c.items.map(({ label, amount }) => ({ label, amount })) : [{ label: 'From spreadsheet', amount: c.to }];
      else patch[c.field] = c.to;
    }
    const wasConfirmed = !!s.months[month]?.people?.[pid]?.confirmed;
    if (wasConfirmed) s = reopenReview(s, month, pid);
    s = updateReview(s, month, pid, patch);
    const r = s.months[month]?.people?.[pid];
    if (r && finite(r.actualNet) && model.byKey[month]) {
      const entry = model.byKey[month].people.find((e) => e.personId === pid);
      if (entry) s = setReviewRaw(s, month, pid, (x) => ({ ...x, confirmed: true, confirmedAt: now, confirmedBy: by, snapshot: snapshotOf(entry) }));
    }
  }
  return s;
}

export const thisMonth = (today) => monthOf(today);
export const nextMonth = (today) => addMonths(monthOf(today), 1);
